import { useState, type ReactNode } from "react";
import { Button, Chip, Combobox, TextArea } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  ProductDetailInput,
  ProductFaqInput,
  ProductHighlightInput,
  ProductNoteInput,
} from "@/lib/api/types";

type LocalizedDraft = {
  ar: string;
  en: string;
};

export type ProductHighlightDraft = {
  id: string;
  text: LocalizedDraft;
};

export type ProductDetailDraft = {
  id: string;
  label: LocalizedDraft;
  value: LocalizedDraft;
};

export type ProductFaqDraft = {
  id: string;
  question: LocalizedDraft;
  answer: LocalizedDraft;
};

export type ProductNoteDraft = {
  id: string;
  kind: ProductNoteInput["kind"];
  text: LocalizedDraft;
};

export type ProductContentDraft = {
  shortDescriptionAr: string;
  shortDescriptionEn: string;
  highlights: ProductHighlightDraft[];
  details: ProductDetailDraft[];
  faq: ProductFaqDraft[];
  notes: ProductNoteDraft[];
};

export type ProductContentValidationErrors = {
  highlights?: string;
  details?: string;
  faq?: string;
  notes?: string;
};

type Props = {
  draft: ProductContentDraft;
  disabled?: boolean;
  errors?: ProductContentValidationErrors;
  onShortDescriptionChange: (
    field: "shortDescriptionAr" | "shortDescriptionEn",
    value: string,
  ) => void;
  onAddHighlight: () => void;
  onMoveHighlight: (id: string, direction: "up" | "down") => void;
  onRemoveHighlight: (id: string) => void;
  onHighlightChange: (id: string, field: "ar" | "en", value: string) => void;
  onAddDetail: () => void;
  onMoveDetail: (id: string, direction: "up" | "down") => void;
  onRemoveDetail: (id: string) => void;
  onDetailChange: (
    id: string,
    part: "label" | "value",
    field: "ar" | "en",
    value: string,
  ) => void;
  onAddFaq: () => void;
  onMoveFaq: (id: string, direction: "up" | "down") => void;
  onRemoveFaq: (id: string) => void;
  onFaqChange: (
    id: string,
    part: "question" | "answer",
    field: "ar" | "en",
    value: string,
  ) => void;
  onAddNote: () => void;
  onMoveNote: (id: string, direction: "up" | "down") => void;
  onRemoveNote: (id: string) => void;
  onNoteChange: (id: string, field: "ar" | "en", value: string) => void;
  onNoteKindChange: (id: string, kind: ProductNoteInput["kind"]) => void;
};

const noteKindOptions = (
  t: (key: string) => string,
): Array<{ label: string; value: ProductNoteInput["kind"] }> => [
  { value: "general", label: t(keys.products.contentNoteKindGeneral) },
  { value: "care", label: t(keys.products.contentNoteKindCare) },
  { value: "included", label: t(keys.products.contentNoteKindIncluded) },
  { value: "usage", label: t(keys.products.contentNoteKindUsage) },
  { value: "packaging", label: t(keys.products.contentNoteKindPackaging) },
];

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

function ChevronRightIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`shrink-0 transition-transform duration-150 ${open ? "rotate-90" : "rotate-0"}`}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Rich Accordion                                                     */
/* ------------------------------------------------------------------ */

type RichAccordionProps = {
  open: boolean;
  onToggle: () => void;
  label: string;
  count: number;
  summary?: string;
  actionLabel: string;
  onAdd: () => void;
  internal?: boolean;
  disabled?: boolean;
  children: ReactNode;
};

function RichAccordion({
  open,
  onToggle,
  label,
  count,
  summary,
  actionLabel,
  onAdd,
  internal = false,
  disabled,
  children,
}: RichAccordionProps) {
  return (
    <div
      className={[
        "overflow-hidden rounded-xl border border-[color:var(--border-light)]",
        open
          ? "bg-[color:var(--surface)]"
          : "bg-[color:var(--background-muted)]",
      ].join(" ")}
    >
      <header
        className={[
          "flex cursor-pointer select-none items-center gap-2.5",
          open
            ? "border-b border-[color:var(--border-light)] px-3.5 pb-2.5 pt-3"
            : "px-3.5 py-2.5",
        ].join(" ")}
        onClick={onToggle}
      >
        <span
          className={
            open
              ? "text-[color:var(--text-secondary)]"
              : "text-[color:var(--text-muted)]"
          }
        >
          <ChevronRightIcon open={open} />
        </span>
        <span
          className={[
            "text-[13px] font-bold uppercase tracking-[0.08em]",
            open
              ? "text-[color:var(--text-primary)]"
              : "text-[color:var(--text-secondary)]",
          ].join(" ")}
        >
          {label}
        </span>
        {count > 0 ? (
          <Chip tone={internal ? "accent" : "neutral"} size="sm">
            {count}
          </Chip>
        ) : null}
        <Button
          variant="link"
          size="sm"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            if (!open) onToggle();
            onAdd();
          }}
          className="ms-auto text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
          leadingIcon={
            <span className="text-[color:var(--accent-600)]">
              <PlusIcon />
            </span>
          }
        >
          {actionLabel}
        </Button>
      </header>
      {open ? (
        <div className="p-3.5">
          {summary ? (
            <div
              className={[
                "mb-3 rounded-lg px-3 py-2 text-meta",
                internal
                  ? "border border-[color-mix(in_oklab,var(--accent-500)_28%,var(--card))] bg-[color-mix(in_oklab,var(--accent-500)_10%,var(--card))] text-[color:var(--accent-600)]"
                  : "border border-[color:var(--border-light)] bg-[color:var(--background-muted)] text-[color:var(--text-secondary)]",
              ].join(" ")}
            >
              {summary}
            </div>
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Small action / helpers                                             */
/* ------------------------------------------------------------------ */

function RowDeleteButton({
  onClick,
  ariaLabel,
  disabled,
}: {
  onClick: () => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="link"
      size="sm"
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={disabled}
      leadingIcon={<TrashIcon />}
      className="shrink-0"
      style={{ color: "var(--text-muted)" }}
    />
  );
}

function MoveButtons({
  index,
  total,
  onMove,
  disabled,
}: {
  index: number;
  total: number;
  onMove: (direction: "up" | "down") => void;
  disabled?: boolean;
}) {
  const t = useTranslations();
  return (
    <div className="flex shrink-0 flex-col">
      <button
        type="button"
        onClick={() => onMove("up")}
        disabled={disabled || index === 0}
        aria-label={t(keys.products.moveUp)}
        className="inline-flex h-5 w-8 items-center justify-center rounded-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-30 lg:h-3.5 lg:w-5"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 15l-6-6-6 6" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => onMove("down")}
        disabled={disabled || index === total - 1}
        aria-label={t(keys.products.moveDown)}
        className="inline-flex h-5 w-8 items-center justify-center rounded-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-30 lg:h-3.5 lg:w-5"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>
  );
}

function EnglishToggle({
  showEn,
  onToggle,
}: {
  showEn: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations();
  return (
    <Button
      variant="link"
      size="sm"
      onClick={onToggle}
      className="self-start"
      style={{ color: "var(--text-muted)" }}
      leadingIcon={
        <span
          className={`inline-flex transition-transform ${showEn ? "rotate-180" : ""}`}
        >
          <ChevronDownIcon />
        </span>
      }
    >
      {showEn
        ? t(keys.products.contentHideEnglish)
        : t(keys.products.contentAddEnglish)}
    </Button>
  );
}

const compactInputClass =
  "h-9 w-full rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2.5 text-card text-[color:var(--text-primary)] outline-none transition-colors placeholder:text-[color:var(--text-muted)] focus:border-[color:var(--accent-500)]";

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export function ProductContentFields({
  draft,
  disabled = false,
  errors,
  onShortDescriptionChange,
  onAddHighlight,
  onMoveHighlight,
  onRemoveHighlight,
  onHighlightChange,
  onAddDetail,
  onMoveDetail,
  onRemoveDetail,
  onDetailChange,
  onAddFaq,
  onMoveFaq,
  onRemoveFaq,
  onFaqChange,
  onAddNote,
  onMoveNote,
  onRemoveNote,
  onNoteChange,
  onNoteKindChange,
}: Props) {
  const t = useTranslations();

  // Per-section open state — match design defaults: highlights open initially.
  const [open, setOpen] = useState<Record<string, boolean>>(() => ({
    highlights: draft.highlights.length > 0,
    details: false,
    faq: false,
    notes: false,
  }));
  const toggle = (k: string) => setOpen((prev) => ({ ...prev, [k]: !prev[k] }));

  // English-field reveal per item id.
  const [showEn, setShowEn] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {};
    for (const h of draft.highlights) if (h.text.en) seed[h.id] = true;
    for (const d of draft.details)
      if (d.label.en || d.value.en) seed[d.id] = true;
    for (const f of draft.faq)
      if (f.question.en || f.answer.en) seed[f.id] = true;
    for (const n of draft.notes) if (n.text.en) seed[n.id] = true;
    return seed;
  });
  const toggleEn = (id: string) =>
    setShowEn((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="flex flex-col gap-4">
      {/* ─── Summary (always visible, no accordion) ─── */}
      <div className="flex flex-col gap-2">
        <div className="text-[13px] font-bold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
          {t(keys.products.contentSectionSummary)}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* English first — primary content for the storefront. */}
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-[color:var(--label-ink)]">
              {t(keys.products.shortDescriptionEn)}
            </span>
            <TextArea
              name="shortDescriptionEn"
              value={draft.shortDescriptionEn}
              onChange={(e) =>
                onShortDescriptionChange("shortDescriptionEn", e.target.value)
              }
              placeholder={t(keys.placeholders.productDescriptionEn)}
            />
            <span className="text-[13px] font-normal text-[color:var(--text-muted)]">
              {t(keys.products.contentEnglishOptionalHelper)}
            </span>
          </label>
          {/* Arabic — required fallback for buyers in Arabic locale. */}
          <label className="flex flex-col gap-1.5">
            <span className="flex items-baseline gap-1 text-[13px] font-medium text-[color:var(--label-ink)]">
              <span className="text-[color:var(--error-500)]">*</span>
              {t(keys.products.shortDescriptionAr)}
            </span>
            <TextArea
              name="shortDescriptionAr"
              value={draft.shortDescriptionAr}
              onChange={(e) =>
                onShortDescriptionChange("shortDescriptionAr", e.target.value)
              }
              placeholder={t(keys.placeholders.productDescriptionAr)}
              dir="rtl"
            />
            <span className="text-[13px] font-normal text-[color:var(--text-muted)]">
              {t(keys.products.contentArabicRequiredHelper)}
            </span>
          </label>
        </div>
      </div>

      {/* ─── Highlights ─── */}
      <RichAccordion
        open={open.highlights ?? false}
        onToggle={() => toggle("highlights")}
        label={t(keys.products.contentSectionHighlights)}
        count={draft.highlights.length}
        summary={t(keys.products.contentSectionHighlightsGuide)}
        actionLabel={t(keys.products.contentAddHighlight)}
        onAdd={onAddHighlight}
        disabled={disabled}
      >
        {errors?.highlights ? (
          <p className="mb-2 text-meta text-[color:var(--error-500)]">
            {errors.highlights}
          </p>
        ) : null}
        <div className="flex flex-col gap-2">
          {draft.highlights.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[color:var(--border-strong)] bg-[color:var(--background-muted)] px-3 py-4 text-center text-meta text-[color:var(--text-muted)]">
              {t(keys.products.contentHighlightsEmpty)}
            </div>
          ) : (
            draft.highlights.map((highlight, index) => {
              const isArOpen = showEn[highlight.id] ?? false;
              return (
                <div
                  key={highlight.id}
                  className="flex flex-col gap-2 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 py-2.5"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--accent-500)_18%,var(--card))] font-mono text-[13px] font-bold text-[color:var(--accent-600)]">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <input
                      value={highlight.text.en}
                      onChange={(e) =>
                        onHighlightChange(highlight.id, "en", e.target.value)
                      }
                      placeholder={t(keys.placeholders.highlightTextEn)}
                      className="flex-1 border-0 bg-transparent px-0 text-[13px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
                    />
                    <MoveButtons
                      index={index}
                      total={draft.highlights.length}
                      disabled={disabled}
                      onMove={(direction) =>
                        onMoveHighlight(highlight.id, direction)
                      }
                    />
                    <RowDeleteButton
                      onClick={() => onRemoveHighlight(highlight.id)}
                      ariaLabel={t(keys.common.remove)}
                      disabled={disabled}
                    />
                  </div>
                  <EnglishToggle
                    showEn={isArOpen}
                    onToggle={() => toggleEn(highlight.id)}
                  />
                  {isArOpen ? (
                    <input
                      value={highlight.text.ar}
                      onChange={(e) =>
                        onHighlightChange(highlight.id, "ar", e.target.value)
                      }
                      placeholder={t(keys.placeholders.highlightTextAr)}
                      dir="rtl"
                      className={compactInputClass}
                    />
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </RichAccordion>

      {/* ─── Additional details ─── */}
      <RichAccordion
        open={open.details ?? false}
        onToggle={() => toggle("details")}
        label={t(keys.products.contentSectionDetails)}
        count={draft.details.length}
        summary={t(keys.products.contentSectionDetailsGuide)}
        actionLabel={t(keys.products.contentAddDetail)}
        onAdd={onAddDetail}
        disabled={disabled}
      >
        {errors?.details ? (
          <p className="mb-2 text-meta text-[color:var(--error-500)]">
            {errors.details}
          </p>
        ) : null}
        <div className="flex flex-col gap-1.5">
          {draft.details.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[color:var(--border-strong)] bg-[color:var(--background-muted)] px-3 py-4 text-center text-meta text-[color:var(--text-muted)]">
              {t(keys.products.contentDetailsEmpty)}
            </div>
          ) : (
            draft.details.map((detail, index) => {
              const isArOpen = showEn[detail.id] ?? false;
              return (
                <div key={detail.id} className="flex flex-col gap-1.5">
                  <div className="grid items-center gap-2 [grid-template-columns:minmax(0,1fr)_auto_auto] sm:[grid-template-columns:minmax(140px,220px)_1fr_auto_auto]">
                    <input
                      value={detail.label.en}
                      onChange={(e) =>
                        onDetailChange(detail.id, "label", "en", e.target.value)
                      }
                      placeholder={t(keys.placeholders.detailLabelEn)}
                      className={`${compactInputClass} col-span-full sm:col-span-1`}
                    />
                    <input
                      value={detail.value.en}
                      onChange={(e) =>
                        onDetailChange(detail.id, "value", "en", e.target.value)
                      }
                      placeholder={t(keys.placeholders.detailValueEn)}
                      className={compactInputClass}
                    />
                    <MoveButtons
                      index={index}
                      total={draft.details.length}
                      disabled={disabled}
                      onMove={(direction) => onMoveDetail(detail.id, direction)}
                    />
                    <RowDeleteButton
                      onClick={() => onRemoveDetail(detail.id)}
                      ariaLabel={t(keys.common.remove)}
                      disabled={disabled}
                    />
                  </div>
                  <EnglishToggle
                    showEn={isArOpen}
                    onToggle={() => toggleEn(detail.id)}
                  />
                  {isArOpen ? (
                    <div className="grid grid-cols-1 gap-2 sm:[grid-template-columns:minmax(140px,220px)_1fr]">
                      <input
                        value={detail.label.ar}
                        onChange={(e) =>
                          onDetailChange(
                            detail.id,
                            "label",
                            "ar",
                            e.target.value,
                          )
                        }
                        placeholder={t(keys.placeholders.detailLabelAr)}
                        dir="rtl"
                        className={compactInputClass}
                      />
                      <input
                        value={detail.value.ar}
                        onChange={(e) =>
                          onDetailChange(
                            detail.id,
                            "value",
                            "ar",
                            e.target.value,
                          )
                        }
                        placeholder={t(keys.placeholders.detailValueAr)}
                        dir="rtl"
                        className={compactInputClass}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </RichAccordion>

      {/* ─── FAQ ─── */}
      <RichAccordion
        open={open.faq ?? false}
        onToggle={() => toggle("faq")}
        label={t(keys.products.contentSectionFaq)}
        count={draft.faq.length}
        summary={t(keys.products.contentSectionFaqGuide)}
        actionLabel={t(keys.products.contentAddFaq)}
        onAdd={onAddFaq}
        disabled={disabled}
      >
        {errors?.faq ? (
          <p className="mb-2 text-meta text-[color:var(--error-500)]">
            {errors.faq}
          </p>
        ) : null}
        <div className="flex flex-col gap-2.5">
          {draft.faq.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[color:var(--border-strong)] bg-[color:var(--background-muted)] px-3 py-4 text-center text-meta text-[color:var(--text-muted)]">
              {t(keys.products.contentFaqEmpty)}
            </div>
          ) : (
            draft.faq.map((item, index) => {
              const isArOpen = showEn[item.id] ?? false;
              return (
                <div
                  key={item.id}
                  className="flex flex-col gap-2 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-3"
                >
                  <div className="flex items-start gap-2.5">
                    <Chip tone="accent" size="sm" className="mt-0.5">
                      Q
                    </Chip>
                    <input
                      value={item.question.en}
                      onChange={(e) =>
                        onFaqChange(item.id, "question", "en", e.target.value)
                      }
                      placeholder={t(keys.placeholders.faqQuestionEn)}
                      className="flex-1 border-0 bg-transparent px-0 py-1 text-[13px] font-medium text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
                    />
                    <MoveButtons
                      index={index}
                      total={draft.faq.length}
                      disabled={disabled}
                      onMove={(direction) => onMoveFaq(item.id, direction)}
                    />
                    <RowDeleteButton
                      onClick={() => onRemoveFaq(item.id)}
                      ariaLabel={t(keys.common.remove)}
                      disabled={disabled}
                    />
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Chip tone="neutral" size="sm" className="mt-1">
                      A
                    </Chip>
                    <textarea
                      value={item.answer.en}
                      onChange={(e) =>
                        onFaqChange(item.id, "answer", "en", e.target.value)
                      }
                      placeholder={t(keys.placeholders.faqAnswerEn)}
                      rows={2}
                      className="min-h-10 flex-1 resize-y border-0 bg-transparent px-0 py-1 text-[13px] leading-relaxed text-[color:var(--text-secondary)] outline-none placeholder:text-[color:var(--text-muted)]"
                    />
                  </div>
                  <EnglishToggle
                    showEn={isArOpen}
                    onToggle={() => toggleEn(item.id)}
                  />
                  {isArOpen ? (
                    <div className="flex flex-col gap-2 border-t border-dashed border-[color:var(--border-light)] pt-2">
                      <div className="flex items-start gap-2.5">
                        <Chip tone="accent" size="sm" className="mt-0.5">
                          Q
                        </Chip>
                        <input
                          value={item.question.ar}
                          onChange={(e) =>
                            onFaqChange(
                              item.id,
                              "question",
                              "ar",
                              e.target.value,
                            )
                          }
                          placeholder={t(keys.placeholders.faqQuestionAr)}
                          dir="rtl"
                          className="flex-1 border-0 bg-transparent px-0 py-1 text-[13px] text-[color:var(--text-secondary)] outline-none placeholder:text-[color:var(--text-muted)]"
                        />
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Chip tone="neutral" size="sm" className="mt-1">
                          A
                        </Chip>
                        <textarea
                          value={item.answer.ar}
                          onChange={(e) =>
                            onFaqChange(item.id, "answer", "ar", e.target.value)
                          }
                          placeholder={t(keys.placeholders.faqAnswerAr)}
                          dir="rtl"
                          rows={2}
                          className="min-h-10 flex-1 resize-y border-0 bg-transparent px-0 py-1 text-[13px] leading-relaxed text-[color:var(--text-secondary)] outline-none placeholder:text-[color:var(--text-muted)]"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </RichAccordion>

      {/* ─── Notes (internal) ─── */}
      <RichAccordion
        open={open.notes ?? false}
        onToggle={() => toggle("notes")}
        label={t(keys.products.contentSectionNotes)}
        count={draft.notes.length}
        summary={t(keys.products.contentSectionNotesGuide)}
        actionLabel={t(keys.products.contentAddNote)}
        onAdd={onAddNote}
        disabled={disabled}
        internal
      >
        {errors?.notes ? (
          <p className="mb-2 text-meta text-[color:var(--error-500)]">
            {errors.notes}
          </p>
        ) : null}
        <div className="flex flex-col gap-2">
          {draft.notes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[color:var(--border-strong)] bg-[color:var(--background-muted)] px-3 py-4 text-center text-meta text-[color:var(--text-muted)]">
              {t(keys.products.contentNotesEmpty)}
            </div>
          ) : (
            draft.notes.map((note, index) => {
              const isArOpen = showEn[note.id] ?? false;
              return (
                <div
                  key={note.id}
                  className="flex flex-col gap-2 rounded-xl border border-[color-mix(in_oklab,var(--accent-500)_22%,var(--card))] bg-[color-mix(in_oklab,var(--accent-500)_6%,var(--card))] px-3 py-2.5"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-[color:var(--accent-600)]">
                      <InfoIcon />
                    </span>
                    <input
                      value={note.text.en}
                      onChange={(e) =>
                        onNoteChange(note.id, "en", e.target.value)
                      }
                      placeholder={t(keys.placeholders.noteTextEn)}
                      className="flex-1 border-0 bg-transparent px-0 text-[13px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
                    />
                    <MoveButtons
                      index={index}
                      total={draft.notes.length}
                      disabled={disabled}
                      onMove={(direction) => onMoveNote(note.id, direction)}
                    />
                    <RowDeleteButton
                      onClick={() => onRemoveNote(note.id)}
                      ariaLabel={t(keys.common.remove)}
                      disabled={disabled}
                    />
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-[13px] uppercase tracking-wider text-[color:var(--text-muted)]">
                      {t(keys.products.contentNoteKindLabel)}
                    </span>
                    <div className="w-44">
                      <Combobox
                        value={note.kind}
                        onChange={(value) => {
                          if (value)
                            onNoteKindChange(
                              note.id,
                              value as ProductNoteInput["kind"],
                            );
                        }}
                        options={noteKindOptions(t)}
                        aria-label={t(keys.products.contentNoteKindLabel)}
                      />
                    </div>
                  </div>
                  <EnglishToggle
                    showEn={isArOpen}
                    onToggle={() => toggleEn(note.id)}
                  />
                  {isArOpen ? (
                    <input
                      value={note.text.ar}
                      onChange={(e) =>
                        onNoteChange(note.id, "ar", e.target.value)
                      }
                      placeholder={t(keys.placeholders.noteTextAr)}
                      dir="rtl"
                      className={compactInputClass}
                    />
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </RichAccordion>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Conversion helpers (input shape ↔ draft shape)                     */
/* ------------------------------------------------------------------ */

export function toProductHighlightDraft(
  input: ProductHighlightInput,
  id: string,
): ProductHighlightDraft {
  return {
    id,
    text: {
      ar: input.text.ar,
      en: input.text.en ?? "",
    },
  };
}

export function toProductDetailDraft(
  input: ProductDetailInput,
  id: string,
): ProductDetailDraft {
  return {
    id,
    label: {
      ar: input.label.ar,
      en: input.label.en ?? "",
    },
    value: {
      ar: input.value.ar,
      en: input.value.en ?? "",
    },
  };
}

export function toProductFaqDraft(
  input: ProductFaqInput,
  id: string,
): ProductFaqDraft {
  return {
    id,
    question: {
      ar: input.question.ar,
      en: input.question.en ?? "",
    },
    answer: {
      ar: input.answer.ar,
      en: input.answer.en ?? "",
    },
  };
}

export function toProductNoteDraft(
  input: ProductNoteInput,
  id: string,
): ProductNoteDraft {
  return {
    id,
    kind: input.kind,
    text: {
      ar: input.text.ar,
      en: input.text.en ?? "",
    },
  };
}
